import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import FlowStatsList from '../../../../../../config/containers/FlowStats/FlowStatsList';
import { isBelowMinPrivilege } from '../../../../../../config/utils/permissions_roles/roles';
import { makeSelectStatisticsMode } from '../../../../App/selectors';
import useFlowData, { ALL_MACHINES_ID } from '../../../../FlowStats/UseFlowData';
import { DEFAULT_LIVE_WINDOW_MINUTES } from './machineStatDataConstants';
import { DEFAULT_LIVE_RANGE_HOURS, EMPTY_STATISTICS_DATASETS, OVERVIEW_DATASET_KEY, SIGNAL_OVERLAP_DATASET_KEY } from './statisticsOverlayConstants';
import { emitStatisticsSummaryUpdated, STATISTICS_SELECT_DATASET_EVENT } from './statisticsOverlayEvents';
import { hoursAgoInput, nowInput } from './statisticsOverlayHelpers';
import useMachineStatData from './useMachineStatData';
import useProductionEffectiveTime from './useProductionEffectiveTime';
import useProductionGapExclusions from './useProductionGapExclusions';
import useProductionStops from './useProductionStops';

export default function useStatisticsOverlayData() {
  const statisticsMode = useSelector(makeSelectStatisticsMode());
  const flowStatsConfig = FlowStatsList?.default ?? FlowStatsList ?? {};
  const privilege = useSelector((state) => state.global?.loginState?.user?.privilege);
  const statisticsDatasets = useMemo(
    () => (flowStatsConfig?.statisticsDatasets ?? EMPTY_STATISTICS_DATASETS).filter((dataset) => !isBelowMinPrivilege(dataset, privilege)),
    [flowStatsConfig, privilege]
  );
  const showOverview = flowStatsConfig?.showOverview === true;
  const showContractEfficiency = flowStatsConfig?.showContractEfficiency !== false;
  const showExpectedContractSpeed = flowStatsConfig?.showExpectedContractSpeed !== false;
  const statisticsDatasetKeys = useMemo(() => statisticsDatasets.map((dataset) => dataset.key).join('|'), [statisticsDatasets]);
  const timeframeSelectionEndpoint = flowStatsConfig?.dataSource?.timeframeSelectionEndpoint ?? '';
  const defaultDatasetKey = flowStatsConfig.defaultDatasetKey ?? (showOverview ? OVERVIEW_DATASET_KEY : SIGNAL_OVERLAP_DATASET_KEY);
  const [selectedDatasetKey, setSelectedDatasetKey] = useState(defaultDatasetKey);
  const [statisticsRangeMode, setStatisticsRangeMode] = useState('live');
  const [showNoFlowHighlights, setShowNoFlowHighlights] = useState(false);
  const excludeNoFlow = flowStatsConfig.noFlowCalculationFollowsVisibility === true && !showNoFlowHighlights;
  const [statisticsFrom, setStatisticsFrom] = useState(() => hoursAgoInput(DEFAULT_LIVE_RANGE_HOURS));
  const [statisticsTo, setStatisticsTo] = useState(() => nowInput());
  const statisticsOverlayGraph = flowStatsConfig?.graphs?.find((graph) => graph.cumulative && graph.statisticsOverlay?.views?.length) ?? null;

  const { allMachineData: statisticsOverlayGraphData } = useFlowData({
    config: statisticsOverlayGraph ? flowStatsConfig : null,
    selectedTab: ALL_MACHINES_ID,
    windowMinutes: statisticsRangeMode === 'live' ? DEFAULT_LIVE_WINDOW_MINUTES : undefined,
    customFrom: statisticsRangeMode === 'range' ? statisticsFrom : undefined,
    customTo: statisticsRangeMode === 'range' ? statisticsTo : undefined,
    liveEnabled: statisticsRangeMode === 'live'
  });

  useEffect(() => {
    if (!statisticsMode) return;

    setStatisticsRangeMode('live');
    setStatisticsFrom(hoursAgoInput(DEFAULT_LIVE_RANGE_HOURS));
    setStatisticsTo(nowInput());
    setSelectedDatasetKey(defaultDatasetKey);
  }, [defaultDatasetKey, statisticsDatasetKeys, statisticsMode]);

  useEffect(() => {
    if (!statisticsMode || typeof window === 'undefined') return undefined;

    const handleDatasetSelection = (event) => {
      const datasetKey = event?.detail?.datasetKey;
      if (!statisticsDatasets.some((dataset) => dataset.key === datasetKey)) return;
      setSelectedDatasetKey(datasetKey);
    };

    window.addEventListener(STATISTICS_SELECT_DATASET_EVENT, handleDatasetSelection);
    return () => window.removeEventListener(STATISTICS_SELECT_DATASET_EVENT, handleDatasetSelection);
  }, [statisticsDatasets, statisticsMode]);

  const {
    dataByArea,
    chartDataByArea,
    summaryByArea: originalSummaryByArea,
    averageLengths,
    loading,
    error,
    lastUpdated
  } = useMachineStatData({
    datasets: statisticsDatasets,
    rangeMode: statisticsRangeMode,
    from: statisticsFrom,
    to: statisticsTo
  });

  const selectedDataset = statisticsDatasets.find((dataset) => dataset.key === selectedDatasetKey);
  const dailyProduction = useProductionEffectiveTime({ dataset: selectedDataset, datasets: statisticsDatasets, excludeNoFlow, rangeMode: statisticsRangeMode, from: statisticsFrom, to: statisticsTo });
  const productionStops = useProductionStops({ dataset: selectedDataset, datasets: statisticsDatasets, dataByArea, rangeMode: statisticsRangeMode, from: statisticsFrom, to: statisticsTo });
  const gapExclusions = useProductionGapExclusions({
    dataset: selectedDataset,
    datasets: statisticsDatasets,
    rangeMode: statisticsRangeMode,
    from: statisticsFrom,
    to: statisticsTo,
    dataByArea,
    summaryByArea: originalSummaryByArea,
    production: dailyProduction,
    stops: productionStops.allStops,
    excludeNoFlow
  });
  const { summaryByArea, efficiencyDataByArea } = gapExclusions;

  useEffect(() => {
    if (!statisticsMode) {
      emitStatisticsSummaryUpdated({ summaryByArea: {}, rangeMode: 'live', from: '', to: '' });
      return;
    }

    emitStatisticsSummaryUpdated({
      summaryByArea,
      rangeMode: statisticsRangeMode,
      from: statisticsFrom,
      to: statisticsTo
    });
  }, [statisticsFrom, statisticsMode, statisticsRangeMode, statisticsTo, summaryByArea]);

  return {
    statisticsMode,
    statisticsDatasets,
    statisticsSections: flowStatsConfig.statisticsSections,
    statisticsOverlayGraph,
    statisticsOverlayGraphData,
    showOverview,
    showNoFlowHighlights,
    setShowNoFlowHighlights,
    signalOverlapLast: flowStatsConfig.signalOverlapLast,
    showContractEfficiency,
    showExpectedContractSpeed,
    selectedDatasetKey,
    setSelectedDatasetKey,
    timeframeSelectionEndpoint,
    statisticsRangeMode,
    setStatisticsRangeMode,
    statisticsFrom,
    setStatisticsFrom,
    statisticsTo,
    setStatisticsTo,
    dataByArea,
    chartDataByArea,
    summaryByArea,
    efficiencyDataByArea,
    dailyProduction,
    gapExclusions,
    productionStops,
    averageLengths,
    loading: loading || (excludeNoFlow && dailyProduction.loading),
    error,
    lastUpdated
  };
}
